import { matchAreaToFilterOption, hasListValueNormalized } from '@/lib/propertyFilterUtils'
import { titleCase } from '@/lib/propertyNavLinks'

export interface AreaFilterOption {
  id: number
  name: string
  city?: string
  count?: number
}

export function isAreaId(value: string): boolean {
  return /^\d+$/.test(value.trim())
}

export function normalizeCityKey(value: string): string {
  return value.trim().toLowerCase()
}

/** Areas belonging to the selected city (when city field is present). */
export function areasForCity(
  areas: readonly AreaFilterOption[],
  city: string,
): AreaFilterOption[] {
  const cityKey = normalizeCityKey(city)
  if (!cityKey) return [...areas]

  return areas.filter((area) => {
    const name = area.name?.trim() ?? ''
    if (!name) return false
    if (!area.city) return true
    return normalizeCityKey(area.city) === cityKey
  })
}

/** Top areas for a city by active listing count (id optional — live API may omit id). */
export function topAreasByCount(
  areas: readonly AreaFilterOption[],
  city: string,
  limit = 6,
): AreaFilterOption[] {
  return areasForCity(areas, city)
    .filter((area) => (area.count ?? 0) > 0)
    .sort((a, b) => (b.count ?? 0) - (a.count ?? 0) || a.name.localeCompare(b.name))
    .slice(0, limit)
}

/** Canonical URL/API filter value — prefer area name for readable URLs. */
export function areaFilterValue(area: AreaFilterOption): string {
  return area.name
}

export function resolveAreaDisplayLabel(
  value: string | undefined,
  areas?: readonly AreaFilterOption[],
): string | null {
  if (!value?.trim()) return null
  if (isAreaId(value) && areas?.length) {
    const match = areas.find((area) => String(area.id) === value.trim())
    if (match) return match.name
  }
  return titleCase(value.replace(/^(?:by|near|at|the)\s+/i, '').trim())
}

export function isAreaFilterSelected(
  selected: string[] | undefined,
  area: AreaFilterOption,
): boolean {
  if (!selected?.length) return false
  const value = areaFilterValue(area)
  if (selected.includes(value)) return true
  return hasListValueNormalized(selected, area.name)
}

export function toggleAreaFilter(
  selected: string[] | undefined,
  area: AreaFilterOption,
): string[] {
  const value = areaFilterValue(area)
  const current = selected ?? []
  if (isAreaFilterSelected(current, area)) {
    return current.filter(
      (item) => item !== value && !hasListValueNormalized([item], area.name),
    )
  }
  const withoutLegacy = current.filter(
    (item) => item !== value && !hasListValueNormalized([item], area.name),
  )
  return [...withoutLegacy, value]
}

/** Normalize mixed area params (legacy ids/names) into canonical area names.
 * Unmatched values are dropped — never keep adjectives like "Fully Furnished" as area.
 */
export function reconcileAreaFilterValues(
  rawAreas: string[],
  areas: readonly AreaFilterOption[],
): string[] {
  const names = areas.map((area) => area.name)
  const out: string[] = []

  for (const raw of rawAreas) {
    const trimmed = raw.trim()
    if (!trimmed) continue

    if (isAreaId(trimmed)) {
      const byId = areas.find((area) => String(area.id) === trimmed)
      if (byId) out.push(byId.name)
      continue
    }

    const byName = areas.find((area) => hasListValueNormalized([trimmed], area.name))
    if (byName) {
      out.push(byName.name)
      continue
    }

    const matchedName = matchAreaToFilterOption(trimmed, names)
    if (matchedName) {
      const byMatch = areas.find((area) => hasListValueNormalized([matchedName], area.name))
      if (byMatch) out.push(byMatch.name)
    }
    // else: drop — not a real locality for this city
  }

  return out
}

export function matchSearchQueryToAreaId(
  query: string,
  areas: readonly AreaFilterOption[],
): string | null {
  const names = areas.map((area) => area.name)
  const matchedName = matchAreaToFilterOption(query, names)
  if (!matchedName) return null

  const area = areas.find((row) => hasListValueNormalized([matchedName], row.name))
  if (!area || area.id <= 0) return null

  return String(area.id)
}

export function areaSuggestionParam(
  item: { id?: number; title: string },
  areas?: readonly AreaFilterOption[],
): string {
  if (item.id && item.id > 0 && areas?.length) {
    const byId = areas.find((area) => area.id === item.id)
    if (byId) return byId.name
  }
  if (areas?.length) {
    const byName = areas.find((area) => hasListValueNormalized([item.title], area.name))
    if (byName) return byName.name
  }
  return item.title
}
